import { useMutation } from '@tanstack/react-query';
import { Col, DatePicker, Form, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { DefaultOptionType } from 'antd/es/select';
import { PlantApi } from 'apis/PlantApis';
import { FormFilter, SelectForm } from 'components';
import RangePickerForm from 'components/RangePicker/RangePickerForm';
import { useUser } from 'contexts/UserProvider';
import dayjs from 'dayjs';
import { IPlant } from 'interfaces/Plant/Plant';
import Content from 'layouts/Content';
import { memo, useCallback, useEffect, useState } from 'react';

const spanCol = 6;

interface IFilterHOFollowCustomer {
  isOpen: boolean;
  plantData: IPlant[];
  defaultFilter: any;
  onFilter(values: any): void;
  onCancel: () => void;
}

const initFilter = {
  year: undefined,
  branch: undefined,
};

const FilterHOFollowPlan = ({
  isOpen,
  plantData,
  defaultFilter,
  onFilter,
  onCancel,
}: IFilterHOFollowCustomer) => {
  const [form] = useForm(),
    { user } = useUser();
  const [minMonth, setMinMonth] = useState(dayjs().month(0));
  const [maxMonth, setMaxMonth] = useState(dayjs());

  const [optionFilter, setOptionFilter] = useState<DefaultOptionType[]>([]);
  const [typeRevenue, setTypeRevenue] = useState([{}]);

  const { mutate: getTypeRevenue } = useMutation({
    mutationFn: () => PlantApi.getTypeRevenueComboBox(),
    onSuccess: (res: any) => {
      setTypeRevenue(
        res.data.getCode.items
          ? res.data.getCode.items.map((item: any) => ({
              value: item.id,
              label: item.name,
            }))
          : [],
      );
    },
    onError: (error) => {
      return console.log(error);
    },
  });

  const setDefaultFilter = useCallback(() => {
    form.setFieldsValue({
      year: dayjs(new Date().getFullYear().toString(), 'YYYY'),
      month: [dayjs().month(0), dayjs()],
    });
  }, [form]);

  const handleYearChange = useCallback(
    (value: any) => {
      const currentYear = dayjs().year();
      const selectedYear = value.year();

      setMinMonth(dayjs().year(selectedYear).month(0));
      setMaxMonth(
        selectedYear === currentYear
          ? dayjs()
          : dayjs().year(selectedYear).month(11),
      );

      form.setFieldsValue({
        year: value,
        month: [
          dayjs().year(selectedYear).month(0),
          selectedYear === currentYear
            ? dayjs()
            : dayjs().year(selectedYear).month(11),
        ],
      });
    },
    [form],
  );

  const handleFinish = (values: any) => {
    onFilter(values);
  };

  const handleCancel = () => {
    if (user?.isCN || user?.isSale) {
      form.setFieldsValue({
        ...initFilter,
        branch: optionFilter[0]?.label,
      });
    } else form.resetFields();

    setDefaultFilter();
    onCancel();
  };

  useEffect(() => {
    setDefaultFilter();
  }, [setDefaultFilter]);

  useEffect(() => {
    const options = plantData.map((plant) => ({
      label: plant.id,
      value: plant.id,
    }));

    setOptionFilter(options);
  }, [plantData]);

  useEffect(() => {
    if (defaultFilter) {
      form.setFieldsValue({
        ...defaultFilter,
        branch: defaultFilter.plantId,
      });
    }
  }, [defaultFilter, form]);

  useEffect(() => {
    if (!(user?.isAdmin || user?.isHO)) {
      form.setFieldsValue({
        year: dayjs(new Date().getFullYear().toString(), 'YYYY'),
      });
    }
  }, [form, user]);

  useEffect(() => {
    getTypeRevenue();
  }, [getTypeRevenue]);

  return (
    <Content isOpen={isOpen}>
      <FormFilter form={form} onFinish={handleFinish} onCancel={handleCancel}>
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Chi nhánh" name="plantId">
              <SelectForm
                options={optionFilter}
                placeholder="Chọn chi nhánh"
                disabled={
                  plantData.length === 1 || !(user?.isAdmin || user?.isHO)
                }
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Loại doanh thu" name="typeRevenue">
              <SelectForm
                options={typeRevenue}
                placeholder="Chọn loại doanh thu"
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Năm" name="year">
              <DatePicker
                allowClear={false}
                name="year"
                picker="year"
                placeholder="Chọn năm"
                className="h-[40px] w-full"
                onChange={handleYearChange}
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Từ tháng" name="month">
              <RangePickerForm
                allowClear={false}
                picker="month"
                format="MM/YYYY"
                placeholder={['Chọn tháng', 'Chọn tháng']}
                maxDate={maxMonth}
                minDate={minMonth}
              />
            </Form.Item>
          </Col>
        </Row>
      </FormFilter>
    </Content>
  );
};

export default memo(FilterHOFollowPlan);
